import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

/**
 * Controle segmentado: a aba ativa é a superfície clara sobre o trilho cinza.
 * Sem régua, sem borda — só a inversão de tom (design.md §7).
 *
 * Na vertical o trilho some. Empilhadas, as abas já formam uma coluna, e a
 * faixa cinza atrás delas viraria um segundo painel dentro do painel — dois
 * tons de cinza encaixados, que é exatamente o que a direção "Cal" evita.
 */
export function TabList({
  label,
  orientation = 'horizontal',
  children,
}: {
  label: string;
  orientation?: 'horizontal' | 'vertical';
  children: ReactNode;
}) {
  const vertical = orientation === 'vertical';

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      className={cn(
        vertical
          ? 'flex w-full flex-col gap-0.5'
          : 'inline-flex h-10 max-w-full items-center gap-1 overflow-x-auto rounded-md bg-muted p-1',
      )}
    >
      {children}
    </div>
  );
}

export function Tab({
  active,
  onClick,
  count,
  icon: Icon,
  disabled = false,
  title,
  vertical = false,
  /** Ponto de aviso à direita: a etapa tem campo com erro. */
  alert = false,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count?: number | undefined;
  icon?: LucideIcon | undefined;
  disabled?: boolean | undefined;
  title?: string | undefined;
  vertical?: boolean | undefined;
  alert?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center gap-2 rounded-sm text-[13px] whitespace-nowrap transition-colors duration-100',
        vertical ? 'h-9 w-full px-3 text-left' : 'h-8 gap-1.5 px-3',
        active
          ? vertical
            ? 'bg-muted font-medium text-foreground'
            : 'bg-card font-medium text-foreground'
          : 'text-muted-foreground hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-50 hover:text-muted-foreground',
      )}
    >
      {Icon ? (
        <Icon
          size={15}
          strokeWidth={1.5}
          className={cn('shrink-0', active && 'text-primary')}
          aria-hidden
        />
      ) : null}
      <span className={cn(vertical && 'min-w-0 flex-1 truncate')}>{children}</span>
      {count === undefined ? null : (
        <span
          className={cn('tabular text-[12px]', active ? 'text-muted-foreground' : 'opacity-70')}
        >
          {count}
        </span>
      )}
      {alert ? (
        <span
          aria-label="Esta etapa tem campos a corrigir"
          className="size-1.5 shrink-0 rounded-full bg-danger"
        />
      ) : null}
    </button>
  );
}
